
import React, { useState, useEffect, lazy, Suspense } from 'react';
import { BrowserRouter as Router, Routes, Route, Link, NavLink, Navigate, useLocation } from 'react-router-dom';
import { Analytics } from '@vercel/analytics/react';
import Home from './pages/Home';
import RequireAuth from './components/RequireAuth';
import { getToken, getUsuario, logout } from './services/auth';
import BrandLogo from './components/BrandLogo';
import AnnouncementBar from './components/AnnouncementBar';
import ErrorBoundary from './components/ErrorBoundary';
import { registarEvento } from './utils/analytics';

const Servicos = lazy(() => import('./pages/Servicos'));
const ServicoDetalhe = lazy(() => import('./pages/ServicoDetalhe'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogPost = lazy(() => import('./pages/BlogPost'));
const Equipe = lazy(() => import('./pages/Equipe'));
const Galeria = lazy(() => import('./pages/Galeria'));
const Horarios = lazy(() => import('./pages/Horarios'));
const Contato = lazy(() => import('./pages/Contato'));
const Admin = lazy(() => import('./pages/Admin'));
const Restrito = lazy(() => import('./pages/Restrito'));
const PoliticaPrivacidade = lazy(() => import('./pages/PoliticaPrivacidade'));
const PoliticaCookies = lazy(() => import('./pages/PoliticaCookies'));
const Login = lazy(() => import('./pages/Login'));
const ResetPassword = lazy(() => import('./pages/ResetPassword'));
const AdminUsuarios = lazy(() => import('./pages/AdminUsuarios'));
const AdminClientes = lazy(() => import('./pages/AdminClientes'));
const AdminServicos = lazy(() => import('./pages/AdminServicos'));
const AdminHorarios = lazy(() => import('./pages/AdminHorarios'));
const AdminAulas = lazy(() => import('./pages/AdminAulas'));
const AdminMarcacoes = lazy(() => import('./pages/AdminMarcacoes'));
const RestritoMarcacoes = lazy(() => import('./pages/RestritoMarcacoes'));
const AdminCalendario = lazy(() => import('./pages/AdminCalendario'));
const AdminProdutos = lazy(() => import('./pages/AdminProdutos'));
const AdminVendas = lazy(() => import('./pages/AdminVendas'));
const AdminTestemunhos = lazy(() => import('./pages/AdminTestemunhos'));
const AdminDespesas = lazy(() => import('./pages/AdminDespesas'));
const PerfilUtilizador = lazy(() => import('./pages/PerfilUtilizador'));
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const NotFound = lazy(() => import('./pages/NotFound'));
const AdminContactos = lazy(() => import('./pages/AdminContactos'));
const AdminComunicacoes = lazy(() => import('./pages/AdminComunicacoes'));
const AdminPacotes = lazy(() => import('./pages/AdminPacotes'));
const AdminBlog = lazy(() => import('./pages/AdminBlog'));
const CookieBanner = lazy(() => import('./components/CookieBanner'));
const WhatsAppButton = lazy(() => import('./components/WhatsAppButton'));
const BackToTop = lazy(() => import('./components/BackToTop'));

function NavBar() {
  const location = useLocation();
  const usuario = getUsuario();
  const [menuAberto, setMenuAberto] = useState(false);
  const [topo, setTopo] = useState(true);

  // Fecha menu quando muda de página
  useEffect(() => { setMenuAberto(false); }, [location.pathname]);

  // Navbar expandida no topo e compacta ao fazer scroll
  useEffect(() => {
    const onScroll = () => setTopo(window.scrollY < 24);
    onScroll();
    window.addEventListener('scroll', onScroll, { passive: true });
    return () => window.removeEventListener('scroll', onScroll);
  }, []);

  const linkClass = ({ isActive }) =>
    isActive
      ? 'text-brand-gold font-semibold border-b-2 border-brand-gold pb-0.5'
      : 'text-stone-300 hover:text-brand-gold transition-colors duration-200';

  const mobileLinkClass = ({ isActive }) =>
    isActive
      ? 'text-brand-gold font-semibold'
      : 'text-stone-300 hover:text-brand-gold transition-colors duration-200';

  return (
    <nav
      className={`sticky top-0 z-50 transition-all duration-300 ${topo ? 'shadow-lg' : 'shadow-md'}`}
      style={{ backgroundColor: '#2E2C29' }}
    >
      <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className={`flex items-center justify-between transition-all duration-300 ${topo ? 'h-20' : 'h-16'}`}>
          {/* Logo / Brand */}
          <Link to="/" className="flex items-center gap-3" onClick={() => setMenuAberto(false)}>
            <BrandLogo
              src="/branding/logo-navbar.png"
              alt="Marcia Monteiro Wellness"
              imgClassName={`w-auto object-contain transition-all duration-300 ${topo ? 'h-14 md:h-16' : 'h-10'}`}
              fallbackClassName="flex flex-col leading-none"
              light={true}
            />
          </Link>

          {/* Desktop links */}
          <div className="hidden md:flex items-center gap-6 text-sm">
            <NavLink to="/" end className={linkClass}>Início</NavLink>
            <NavLink to="/servicos" className={linkClass}>Serviços</NavLink>
            <NavLink to="/equipe" className={linkClass}>Equipa</NavLink>
            <NavLink to="/galeria" className={linkClass}>Galeria</NavLink>
            <NavLink to="/horarios" className={linkClass}>Horários</NavLink>
            <NavLink to="/blog" className={linkClass}>Blog</NavLink>
            <NavLink to="/contato" className={linkClass}>Contacto</NavLink>
            {usuario && (
              <div className="flex items-center gap-3">
                <NavLink to={usuario.tipo === 'admin' ? '/admin' : '/restrito'} className="text-stone-300 hover:text-brand-gold text-sm transition">{usuario.nome}</NavLink>
                <button onClick={logout} className="text-xs px-3 py-1 rounded-full border border-stone-600 text-stone-400 hover:border-red-500 hover:text-red-400 transition">Sair</button>
              </div>
            )}
          </div>

          {/* Hamburger */}
          <button
            className="md:hidden p-2 rounded-md hover:bg-stone-700 transition"
            onClick={() => setMenuAberto(!menuAberto)}
            aria-label="Menu"
          >
            <div className={`w-5 h-0.5 bg-white mb-1 transition-all ${menuAberto ? 'rotate-45 translate-y-1.5' : ''}`}></div>
            <div className={`w-5 h-0.5 bg-white mb-1 transition-all ${menuAberto ? 'opacity-0' : ''}`}></div>
            <div className={`w-5 h-0.5 bg-white transition-all ${menuAberto ? '-rotate-45 -translate-y-1.5' : ''}`}></div>
          </button>
        </div>

        {/* Mobile menu */}
        {menuAberto && (
          <div className="md:hidden pb-5 flex flex-col gap-4 text-sm border-t pt-4" style={{ borderColor: '#3E3C39' }}>
            <NavLink to="/" end className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Início</NavLink>
            <NavLink to="/servicos" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Serviços</NavLink>
            <NavLink to="/equipe" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Equipa</NavLink>
            <NavLink to="/galeria" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Galeria</NavLink>
            <NavLink to="/horarios" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Horários</NavLink>
            <NavLink to="/blog" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Blog</NavLink>
            <NavLink to="/contato" className={mobileLinkClass} onClick={() => setMenuAberto(false)}>Contacto</NavLink>
            {usuario && (
              <>
                <NavLink to={usuario.tipo === 'admin' ? '/admin' : '/restrito'} className="text-stone-300 hover:text-brand-gold transition" onClick={() => setMenuAberto(false)}>{usuario.nome}</NavLink>
                <button onClick={() => { logout(); setMenuAberto(false); }} className="text-left text-sm text-stone-400 hover:text-red-400 transition">Sair</button>
              </>
            )}
          </div>
        )}
      </div>
    </nav>
  );
}

function Footer() {
  return (
    <footer className="mt-16 py-12 px-4 text-sm" style={{ backgroundColor: '#2E2C29', color: '#9c9890' }}>
      <div className="max-w-6xl mx-auto grid grid-cols-1 md:grid-cols-3 gap-8">
        <div>
          {/* Logo footer */}
          <div className="mb-4">
            <BrandLogo
              src="/branding/logo-footer.png"
              alt="Marcia Monteiro Wellness"
              imgClassName="h-10 w-auto object-contain"
              fallbackClassName="flex flex-col leading-none"
              loading="lazy"
              decoding="async"
            />
          </div>
          <p className="leading-relaxed text-stone-400">Espaço de bem-estar dedicado ao equilíbrio do corpo e da mente, com Yoga, Pilates e terapias holísticas.</p>
        </div>
        <div>
          <h4 className="font-semibold mb-4 tracking-wide text-xs uppercase" style={{ color: '#B89547' }}>Navegação</h4>
          <ul className="space-y-2">
            <li><Link to="/servicos" className="hover:text-stone-200 transition">Serviços</Link></li>
            <li><Link to="/equipe" className="hover:text-stone-200 transition">Equipa</Link></li>
            <li><Link to="/galeria" className="hover:text-stone-200 transition">Galeria</Link></li>
            <li><Link to="/horarios" className="hover:text-stone-200 transition">Horários</Link></li>
            <li><Link to="/blog" className="hover:text-stone-200 transition">Blog</Link></li>
            <li><Link to="/contato" className="hover:text-stone-200 transition">Contacto</Link></li>
            <li><Link to="/login" className="hover:text-stone-200 transition">Área Reservada</Link></li>
          </ul>
        </div>
        <div>
          <h4 className="font-semibold mb-4 tracking-wide text-xs uppercase" style={{ color: '#B89547' }}>Contacto</h4>
          <ul className="space-y-2">
            <li>📍 Praceta de Santa Justa, loja 10, Gondifelos</li>
            <li><a href="tel:+351932306390" onClick={() => registarEvento('clique_telefone', { origem: 'rodape' })} className="hover:text-stone-200 transition">📞 (+351) 932 306 390</a></li>
            <li><a href="mailto:marciamonteiro.wellness@gmail.com" className="hover:text-stone-200 transition">✉️ marciamonteiro.wellness@gmail.com</a></li>
            <li>
              <a href="https://wa.me/351932306390" target="_blank" rel="noopener noreferrer" onClick={() => registarEvento('clique_whatsapp', { origem: 'rodape' })} className="hover:text-stone-200 transition inline-flex items-center gap-2">
                <img src="/branding/social/whatsapp.png" alt="WhatsApp" width="16" height="16" className="w-4 h-4 object-contain" loading="lazy" decoding="async" />
                <span>WhatsApp</span>
              </a>
            </li>
            <li>
              <a href="https://www.instagram.com/marcia.monteiro.wellness" target="_blank" rel="noopener noreferrer" className="hover:text-stone-200 transition inline-flex items-center gap-2">
                <img src="/branding/social/instagram.png" alt="Instagram" width="16" height="16" className="w-4 h-4 object-contain" loading="lazy" decoding="async" />
                <span>Instagram</span>
              </a>
            </li>
            <li>
              <a href="https://www.facebook.com/Marcia.Monteiro.Wellness/" target="_blank" rel="noopener noreferrer" className="hover:text-stone-200 transition inline-flex items-center gap-2">
                <img src="/branding/social/facebook.png" alt="Facebook" width="16" height="16" className="w-4 h-4 object-contain" loading="lazy" decoding="async" />
                <span>Facebook</span>
              </a>
            </li>
          </ul>
        </div>
      </div>
      <div className="max-w-6xl mx-auto mt-10 pt-6 text-center text-xs space-y-1" style={{ borderTop: '1px solid #3E3C39' }}>
        <div style={{ color: '#6b6760' }}>© {new Date().getFullYear()} Márcia Monteiro Wellness. Todos os direitos reservados.</div>
        <div className="flex justify-center gap-4">
          <Link to="/politica-privacidade" className="hover:text-stone-200 transition">Política de Privacidade</Link>
          <span>·</span>
          <Link to="/politica-cookies" className="hover:text-stone-200 transition">Política de Cookies</Link>
        </div>
      </div>
    </footer>
  );
}

// Protege as páginas: um erro numa página não deixa o site todo em branco
function PaginasProtegidas({ children }) {
  const { pathname } = useLocation();
  return <ErrorBoundary key={pathname}>{children}</ErrorBoundary>;
}

function App() {
  // Validar token no arranque — desloga se expirado ou inválido
  useEffect(() => {
    const token = getToken();
    if (!token) return;
    const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:4000/api';
    fetch(`${API_URL}/auth/me`, { headers: { Authorization: `Bearer ${token}` } })
      .then(res => { if (!res.ok) logout(); })
      .catch(() => {}); // se o backend estiver em baixo, não desloga
  }, []);
  return (
    <Router>
      <div className="min-h-screen flex flex-col" style={{ backgroundColor: '#FEFEFD' }}>
        <AnnouncementBar />
        <NavBar />
        <main className="flex-1">
          <PaginasProtegidas>
          <Suspense fallback={<div className="py-10 text-center text-stone-500">A carregar...</div>}>
            <Routes>
              <Route path="/" element={<Home />} />
              <Route path="/servicos" element={<Servicos />} />
              <Route path="/servicos/:slug" element={<ServicoDetalhe />} />
              <Route path="/equipe" element={<Equipe />} />
              <Route path="/galeria" element={<Galeria />} />
              <Route path="/horarios" element={<Horarios />} />
              <Route path="/blog" element={<Blog />} />
              <Route path="/blog/:slug" element={<BlogPost />} />
              <Route path="/contato" element={<Contato />} />
              <Route path="/politica-privacidade" element={<PoliticaPrivacidade />} />
              <Route path="/politica-cookies" element={<PoliticaCookies />} />
              <Route path="/login" element={<Login />} />
              <Route path="/reset-password" element={<ResetPassword />} />
              <Route path="/admin/*" element={<RequireAuth adminOnly={true}><Admin /></RequireAuth>}>
                <Route path="usuarios" element={<AdminUsuarios />} />
                <Route path="clientes" element={<AdminClientes />} />
                <Route path="servicos" element={<AdminServicos />} />
                <Route path="horarios" element={<AdminHorarios />} />
                <Route path="aulas" element={<AdminAulas />} />
                <Route path="marcacoes" element={<AdminMarcacoes />} />
                <Route path="minhas-marcacoes" element={<RestritoMarcacoes />} />
                <Route path="calendario" element={<AdminCalendario />} />
                <Route path="produtos" element={<AdminProdutos />} />
                <Route path="vendas" element={<AdminVendas />} />
                <Route path="pacotes" element={<AdminPacotes />} />
                <Route path="blog" element={<AdminBlog />} />
                <Route path="testemunhos" element={<AdminTestemunhos />} />
                <Route path="contactos" element={<AdminContactos />} />
                <Route path="comunicacoes" element={<AdminComunicacoes />} />
                <Route path="despesas" element={<AdminDespesas />} />
                <Route path="perfil" element={<PerfilUtilizador />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route index element={<Navigate to="dashboard" replace />} />
              </Route>
              <Route path="/restrito/*" element={<RequireAuth><Restrito /></RequireAuth>} />
              <Route path="*" element={<NotFound />} />
            </Routes>
          </Suspense>
          </PaginasProtegidas>
        </main>
        <Footer />
        <Suspense fallback={null}>
          <CookieBanner />
          <WhatsAppButton />
          <BackToTop />
        </Suspense>
        <Analytics />
      </div>
    </Router>
  );
}

export default App;
